import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Linkedin, Pencil, LogOut, Save, X, MessageSquare } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/UserAvatar";
import { PostCard } from "@/components/PostCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fullName, memberTypeColor } from "@/lib/format";
import { AccountSettings } from "@/components/AccountSettings";
import { toast } from "sonner";

export default function Profile() {
  const params = useParams();
  const navigate = useNavigate();
  const { user: me, logout, setUser } = useAuth();

  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [meta, setMeta] = useState({ colleges: [], member_types: [] });

  const targetId = params.id || me?.id;
  const isSelf = !params.id || params.id === me?.id;

  useEffect(() => {
    api.get("/meta").then(({ data }) => setMeta(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!targetId) return;
    setLoading(true);
    Promise.all([
      isSelf ? Promise.resolve({ data: me }) : api.get(`/users/${targetId}`),
      api.get(`/users/${targetId}/posts`),
    ])
      .then(([u, p]) => {
        setProfile(u.data);
        setPosts(p.data);
      })
      .catch(() => toast.error("Could not load profile"))
      .finally(() => setLoading(false));
  }, [targetId, isSelf, me]);

  if (loading || !profile) {
    return <div className="text-center py-12 text-muted-foreground" data-testid="profile-loading">Loading profile…</div>;
  }

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl border border-border cornell-shadow p-5" data-testid="profile-card">
        <div className="flex items-start gap-4">
          <UserAvatar user={profile} size={80} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-extrabold tracking-tight">{fullName(profile)}</h1>
              {profile.role === "admin" && (
                <span className="text-[10px] uppercase tracking-wider bg-[#B31B1B] text-white px-1.5 py-0.5 rounded">Admin</span>
              )}
            </div>
            <div className="text-sm text-muted-foreground mt-0.5">
              {profile.college} · Class of {profile.grad_year}
            </div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span className={`text-[11px] px-2.5 py-0.5 rounded-full font-medium ${memberTypeColor(profile.member_type)}`}>
                {profile.member_type}
              </span>
              {profile.linkedin_url && (
                <a
                  href={profile.linkedin_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[11px] inline-flex items-center gap-1 text-sky-700 hover:underline"
                  data-testid="profile-linkedin-link"
                >
                  <Linkedin className="w-3 h-3" /> LinkedIn
                </a>
              )}
            </div>
          </div>
        </div>
        {profile.bio && (
          <p className="mt-4 text-[15px] leading-relaxed text-neutral-800" data-testid="profile-bio">{profile.bio}</p>
        )}

        {isSelf && !editing && (
          <div className="mt-5 flex gap-2">
            <Button
              variant="outline"
              onClick={() => setEditing(true)}
              className="gap-2"
              data-testid="profile-edit-btn"
            >
              <Pencil className="w-4 h-4" /> Edit profile
            </Button>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="gap-2 text-neutral-600"
              data-testid="profile-logout-btn"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </Button>
          </div>
        )}

        {!isSelf && (
          <div className="mt-5 flex gap-2">
            {profile.dm_preference === "none" ? (
              <div className="text-xs text-muted-foreground bg-neutral-50 border border-border rounded-lg px-3 py-2" data-testid="profile-dm-off">
                {profile.first_name} is not accepting direct messages.
              </div>
            ) : (
              <Button
                onClick={() => navigate(`/messages/${profile.id}`)}
                className="gap-2 bg-[#B31B1B] hover:bg-[#8f1616]"
                data-testid="profile-message-btn"
              >
                <MessageSquare className="w-4 h-4" /> Message
              </Button>
            )}
          </div>
        )}
      </div>

      {isSelf && editing && (
        <EditProfileForm
          profile={profile}
          meta={meta}
          onCancel={() => setEditing(false)}
          onSaved={(u) => { setProfile(u); setUser(u); setEditing(false); toast.success("Profile updated"); }}
        />
      )}

      {isSelf && editing && (
        <AccountSettings
          currentEmail={profile.email}
          onEmailChanged={(u) => { setProfile(u); setUser(u); }}
        />
      )}

      <div>
        <h2 className="text-xl font-extrabold tracking-tight mb-3">Posts</h2>
        {posts.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground bg-white rounded-2xl border border-border" data-testid="profile-no-posts">
            No posts yet.
          </div>
        ) : (
          <div className="space-y-4" data-testid="profile-posts-list">
            {posts.map((p) => (
              <PostCard
                key={p.id}
                post={p}
                onChange={(u) => setPosts((prev) => prev.map((x) => (x.id === u.id ? u : x)))}
                onDelete={(id) => setPosts((prev) => prev.filter((x) => x.id !== id))}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function EditProfileForm({ profile, meta, onCancel, onSaved }) {
  const [form, setForm] = useState({
    first_name: profile.first_name,
    last_name: profile.last_name,
    college: profile.college,
    grad_year: profile.grad_year,
    member_type: profile.member_type,
    bio: profile.bio || "",
    linkedin_url: profile.linkedin_url || "",
    photo_data: profile.photo_data || "",
    dm_preference: profile.dm_preference || "anyone",
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 800 * 1024) { toast.error("Photo must be under 800KB"); return; }
    const r = new FileReader();
    r.onload = () => set("photo_data", r.result);
    r.readAsDataURL(file);
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { ...form, grad_year: parseInt(form.grad_year, 10) };
      const { data } = await api.patch("/auth/me", payload);
      onSaved(data);
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const gradYears = Array.from({ length: 2035 - 1955 + 1 }, (_, i) => 2035 - i);

  return (
    <div className="bg-white rounded-2xl border border-border cornell-shadow p-5 space-y-4" data-testid="profile-edit-form">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-extrabold">Edit profile</h2>
        <Button variant="ghost" size="sm" onClick={onCancel} data-testid="profile-edit-cancel-btn"><X className="w-4 h-4" /></Button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>First name</Label><Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="edit-first-name" /></div>
        <div><Label>Last name</Label><Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="edit-last-name" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>College</Label>
          <Select value={form.college} onValueChange={(v) => set("college", v)}>
            <SelectTrigger data-testid="edit-college"><SelectValue /></SelectTrigger>
            <SelectContent>{meta.colleges.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Grad year</Label>
          <Select value={String(form.grad_year)} onValueChange={(v) => set("grad_year", v)}>
            <SelectTrigger data-testid="edit-grad-year"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">{gradYears.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label>Member type</Label>
        <Select value={form.member_type} onValueChange={(v) => set("member_type", v)}>
          <SelectTrigger data-testid="edit-member-type"><SelectValue /></SelectTrigger>
          <SelectContent>{meta.member_types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label>Bio <span className="text-xs text-muted-foreground">({form.bio.length}/150)</span></Label>
        <Textarea maxLength={150} rows={2} value={form.bio} onChange={(e) => set("bio", e.target.value)} data-testid="edit-bio" />
      </div>
      <div>
        <Label>LinkedIn URL</Label>
        <Input value={form.linkedin_url} onChange={(e) => set("linkedin_url", e.target.value)} data-testid="edit-linkedin" />
      </div>
      <div>
        <Label>Profile photo</Label>
        <div className="flex items-center gap-3">
          <UserAvatar user={{ ...profile, ...form }} size={56} />
          <Input type="file" accept="image/*" onChange={onPhoto} className="max-w-xs" data-testid="edit-photo" />
        </div>
      </div>
      <div>
        <Label>Direct message preference</Label>
        <Select value={form.dm_preference} onValueChange={(v) => set("dm_preference", v)}>
          <SelectTrigger data-testid="edit-dm-preference"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="anyone" data-testid="edit-dm-preference-anyone">Anyone can message me</SelectItem>
            <SelectItem value="none" data-testid="edit-dm-preference-none">No messages (opt out)</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-[11px] text-muted-foreground mt-1">Messages stay within the app — email and phone are never shared.</p>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} data-testid="edit-cancel-btn">Cancel</Button>
        <Button onClick={submit} disabled={saving} className="bg-[#B31B1B] hover:bg-[#8f1616] gap-2" data-testid="edit-save-btn">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}
